/**
 * Timeline — the immutable parametric history document.
 *
 * A Timeline is an ordered list of FeatureNodes plus a rollback marker.
 * List order IS the topological order of the DAG: every node's dependencies
 * must appear before it. All mutating operations return a NEW Timeline that
 * structurally shares unchanged nodes, so keeping snapshots for undo/redo
 * costs O(1) per edit.
 */

import {
  type FeatureId,
  type FeatureNode,
  type FeatureParamsByType,
  type FeatureType,
  isFeatureRef,
} from './types';

let idCounter = 0;

export function newFeatureId(): FeatureId {
  // Time component keeps ids unique across sessions; counter within a session.
  return `f_${Date.now().toString(36)}_${(idCounter++).toString(36)}` as FeatureId;
}

/** Walk a params object and collect every FeatureRef's target id (deduped). */
export function collectDependencies(params: unknown): FeatureId[] {
  const deps = new Set<FeatureId>();
  const visit = (v: unknown): void => {
    if (v === null || typeof v !== 'object') return;
    if (isFeatureRef(v)) {
      deps.add(v.$feature);
      return;
    }
    for (const child of Object.values(v)) visit(child);
  };
  visit(params);
  return [...deps];
}

/** ES2022-safe replacement for Array.prototype.with (Safari < 16.4). */
function arrayWith<T>(arr: readonly T[], index: number, value: T): T[] {
  const copy = arr.slice();
  copy[index] = value;
  return copy;
}

function deepFreeze<T>(obj: T): T {
  if (obj !== null && typeof obj === 'object' && !ArrayBuffer.isView(obj)) {
    Object.freeze(obj);
    for (const v of Object.values(obj)) deepFreeze(v);
  }
  return obj;
}

export class TimelineError extends Error {
  override name = 'TimelineError';
}

export class Timeline {
  private readonly index: ReadonlyMap<FeatureId, number>;

  private constructor(
    public readonly features: readonly FeatureNode[],
    /** Features at positions [0, rollbackIndex) are active; the rest are "future". */
    public readonly rollbackIndex: number,
    /** Document parameter table: name → expression ("width" → "60"). */
    public readonly params: Readonly<Record<string, string>> = {},
  ) {
    const index = new Map<FeatureId, number>();
    features.forEach((f, i) => index.set(f.id, i));
    this.index = index;
  }

  static empty(): Timeline {
    return new Timeline([], 0);
  }

  get(id: FeatureId): FeatureNode | undefined {
    const i = this.index.get(id);
    return i === undefined ? undefined : this.features[i];
  }

  positionOf(id: FeatureId): number {
    const i = this.index.get(id);
    if (i === undefined) throw new TimelineError(`Unknown feature: ${id}`);
    return i;
  }

  /** Features currently evaluated (before the rollback bar, not suppressed). */
  get activeFeatures(): readonly FeatureNode[] {
    return this.features.slice(0, this.rollbackIndex);
  }

  /** Direct dependents of `id` (downstream neighbors in the DAG). */
  dependentsOf(id: FeatureId): FeatureId[] {
    return this.features.filter((f) => f.dependencies.includes(id)).map((f) => f.id);
  }

  /** `id` plus everything transitively downstream of it, in timeline order. */
  downstreamOf(id: FeatureId): FeatureId[] {
    const affected = new Set<FeatureId>([id]);
    for (const f of this.features) {
      if (f.dependencies.some((d) => affected.has(d))) affected.add(f.id);
    }
    return this.features.filter((f) => affected.has(f.id)).map((f) => f.id);
  }

  // -------------------------------------------------------------------------
  // Mutating operations — each returns a new Timeline
  // -------------------------------------------------------------------------

  /**
   * Insert a new feature at the rollback bar (like Fusion: creating while
   * rolled back inserts mid-history). Returns the new timeline and the node.
   */
  addFeature<T extends FeatureType>(
    type: T,
    name: string,
    params: FeatureParamsByType[T],
  ): { timeline: Timeline; feature: FeatureNode<T> } {
    const dependencies = collectDependencies(params);
    for (const dep of dependencies) {
      const pos = this.index.get(dep);
      if (pos === undefined) throw new TimelineError(`Dependency ${dep} does not exist`);
      if (pos >= this.rollbackIndex) {
        throw new TimelineError(
          `Dependency ${dep} is after the rollback bar and cannot be referenced`,
        );
      }
    }
    const feature = deepFreeze({
      id: newFeatureId(),
      type,
      name,
      params,
      dependencies,
      suppressed: false,
    }) as FeatureNode<T>;

    const features = [
      ...this.features.slice(0, this.rollbackIndex),
      feature,
      ...this.features.slice(this.rollbackIndex),
    ];
    return { timeline: new Timeline(features, this.rollbackIndex + 1, this.params), feature };
  }

  /** Replace a feature's params (a parametric edit). Position is preserved. */
  updateFeature<T extends FeatureType>(
    id: FeatureId,
    params: FeatureParamsByType[T],
  ): Timeline {
    const pos = this.positionOf(id);
    const old = this.features[pos]!;
    const dependencies = collectDependencies(params);
    for (const dep of dependencies) {
      const depPos = this.index.get(dep);
      if (depPos === undefined) throw new TimelineError(`Dependency ${dep} does not exist`);
      if (depPos >= pos) {
        throw new TimelineError(
          `Edit would make ${old.name} depend on a later feature (cycle)`,
        );
      }
    }
    const next = deepFreeze({ ...old, params, dependencies }) as FeatureNode;
    return new Timeline(arrayWith(this.features, pos, next), this.rollbackIndex, this.params);
  }

  renameFeature(id: FeatureId, name: string): Timeline {
    const pos = this.positionOf(id);
    const next = deepFreeze({ ...this.features[pos]!, name }) as FeatureNode;
    return new Timeline(arrayWith(this.features, pos, next), this.rollbackIndex, this.params);
  }

  setSuppressed(id: FeatureId, suppressed: boolean): Timeline {
    const pos = this.positionOf(id);
    const next = deepFreeze({ ...this.features[pos]!, suppressed }) as FeatureNode;
    return new Timeline(arrayWith(this.features, pos, next), this.rollbackIndex, this.params);
  }

  /** Assign/clear a body's material + optional color override (render-only). */
  setMaterial(id: FeatureId, material: string | undefined, color?: string): Timeline {
    const pos = this.positionOf(id);
    const node = { ...this.features[pos]! } as FeatureNode & {
      material?: string;
      materialColor?: string;
    };
    if (material === undefined) delete node.material;
    else node.material = material;
    if (color === undefined) delete node.materialColor;
    else node.materialColor = color;
    const next = deepFreeze(node) as FeatureNode;
    return new Timeline(arrayWith(this.features, pos, next), this.rollbackIndex, this.params);
  }

  /**
   * Delete a feature. Throws if anything depends on it unless `cascade`,
   * which also deletes the entire downstream cone (Fusion-style prompt is
   * the UI's job; the kernel just enforces integrity).
   */
  removeFeature(id: FeatureId, opts: { cascade?: boolean } = {}): Timeline {
    const pos = this.positionOf(id);
    const dependents = this.dependentsOf(id);
    if (dependents.length > 0 && !opts.cascade) {
      throw new TimelineError(
        `${this.features[pos]!.name} has ${dependents.length} dependent feature(s); ` +
          `delete with cascade or remove them first`,
      );
    }
    const doomed = new Set(opts.cascade ? this.downstreamOf(id) : [id]);
    let removedBeforeBar = 0;
    for (const fid of doomed) {
      if (this.positionOf(fid) < this.rollbackIndex) removedBeforeBar++;
    }
    const features = this.features.filter((f) => !doomed.has(f.id));
    return new Timeline(features, this.rollbackIndex - removedBeforeBar, this.params);
  }

  /**
   * Move a feature to a new position, if the DAG allows it: it must stay
   * after all its dependencies and before all its dependents.
   */
  reorderFeature(id: FeatureId, toPosition: number): Timeline {
    const from = this.positionOf(id);
    const node = this.features[from]!;
    const to = Math.max(0, Math.min(this.features.length - 1, toPosition));
    if (to === from) return this;

    const without = this.features.filter((f) => f.id !== id);
    const reordered = [...without.slice(0, to), node, ...without.slice(to)];

    // Validate topological order after the move.
    const posAfter = new Map(reordered.map((f, i) => [f.id, i] as const));
    for (const f of reordered) {
      for (const dep of f.dependencies) {
        if (posAfter.get(dep)! >= posAfter.get(f.id)!) {
          throw new TimelineError(
            `Cannot move ${node.name} there: it would break dependency order`,
          );
        }
      }
    }
    return new Timeline(reordered, this.rollbackIndex, this.params);
  }

  /**
   * Set (or add) a named document parameter. `expr` is an expression string
   * — "60", "width/2", "2*pi" — evaluated at regeneration time, so every
   * feature using it re-evaluates automatically (resolved values feed the
   * content hash).
   */
  setParam(name: string, expr: string): Timeline {
    const trimmed = name.trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) {
      throw new TimelineError(
        `"${trimmed}" is not a valid parameter name (letters, digits, _; must not start with a digit)`,
      );
    }
    if (!expr.trim()) throw new TimelineError('Parameter expression cannot be empty');
    return new Timeline(this.features, this.rollbackIndex, {
      ...this.params,
      [trimmed]: expr.trim(),
    });
  }

  deleteParam(name: string): Timeline {
    if (!(name in this.params)) return this;
    const next = { ...this.params };
    delete next[name];
    return new Timeline(this.features, this.rollbackIndex, next);
  }

  /** Drag the rollback bar: only features before it are evaluated/shown. */
  setRollback(index: number): Timeline {
    const clamped = Math.max(0, Math.min(this.features.length, index));
    return clamped === this.rollbackIndex ? this : new Timeline(this.features, clamped, this.params);
  }

  // -------------------------------------------------------------------------
  // Serialization (IndexedDB persistence)
  // -------------------------------------------------------------------------

  toJSON(): TimelineJSON {
    return {
      version: 1,
      rollbackIndex: this.rollbackIndex,
      features: this.features as FeatureNode[],
      params: this.params,
    };
  }

  static fromJSON(json: TimelineJSON): Timeline {
    if (json.version !== 1) throw new TimelineError(`Unsupported document version ${json.version}`);
    const features = json.features.map((f) => deepFreeze({ ...f }) as FeatureNode);
    return new Timeline(features, json.rollbackIndex, json.params ?? {});
  }
}

export interface TimelineJSON {
  version: 1;
  rollbackIndex: number;
  features: FeatureNode[];
  /** Document parameter table (absent in pre-expression documents). */
  params?: Record<string, string>;
}
